import React, { useState, useRef } from 'react';
import { Save, X, Upload, AlertCircle, CheckCircle, Info, MessageSquare, Calendar as CalendarIcon } from 'lucide-react';
import { AdminHeader } from '../../components/AdminHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const EntryForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    priority: 'medium',
    description: '',
    category: '',
    status: 'pending',
    autocompleteValue: '',
    selectedImage: null,
    imagePreview: null,
    projectDate: null
  });

  const [autocompleteOptions] = useState([
    'Web Development',
    'Mobile App Development',
    'UI/UX Design',
    'Cloud Solutions',
    'AI Integration',
    'Data Analytics',
    'DevOps Services',
    'Cybersecurity',
    'Blockchain Development',
    'IoT Solutions'
  ]);

  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [messages, setMessages] = useState([
    { id: 1, text: 'Welcome! Fill in the form and submit to see notifications here', type: 'success', timestamp: new Date().toLocaleTimeString() }
  ]);

  const playTypingSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 600 + Math.random() * 200;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.05, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.05);
    } catch (error) {
      // Silently fail if audio context not available
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    playTypingSound();
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAutocompleteChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, autocompleteValue: value }));
    
    if (value.length > 0) {
      const filtered = autocompleteOptions.filter(option =>
        option.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredOptions(filtered);
      setShowAutocomplete(true);
    } else {
      setFilteredOptions([]);
      setShowAutocomplete(false);
    }
  };

  const handleAutocompleteSelect = (option) => {
    setFormData(prev => ({ ...prev, autocompleteValue: option }));
    setShowAutocomplete(false);
    setFilteredOptions([]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            selectedImage: file,
            imagePreview: reader.result
          }));
        };
        reader.readAsDataURL(file);
      } else {
        showMessageAlert('Please select a valid image file', 'error');
      }
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      selectedImage: null,
      imagePreview: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({ ...prev, projectDate: date }));
    setIsDatePickerOpen(false);
  };

  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Audio context not available');
    }
  };

  const showMessageAlert = (message, type = 'success') => {
    playNotificationSound();
    
    const newMessage = {
      id: Date.now(),
      text: message,
      type: type,
      timestamp: new Date().toLocaleTimeString()
    };
    setMessages(prev => [newMessage, ...prev].slice(0, 5));
    
    if (type === 'success') {
      toast.success(message, {
        icon: <CheckCircle className="text-green-500" />,
        duration: 4000,
        style: {
          background: 'rgba(30, 64, 98, 0.95)',
          border: '1px solid #00a8ff',
          color: '#00a8ff',
          boxShadow: '0 0 20px rgba(0, 168, 255, 0.5)'
        }
      });
    } else {
      toast.error(message, {
        icon: <AlertCircle className="text-red-500" />,
        duration: 4000,
        style: {
          background: 'rgba(30, 64, 98, 0.95)',
          border: '1px solid #ff0000',
          color: '#ff6b6b',
          boxShadow: '0 0 20px rgba(255, 0, 0, 0.5)'
        }
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation with specific error messages
    if (!formData.fullName) {
      showMessageAlert('❌ Full Name is required! Please enter your full name.', 'error');
      return;
    }
    
    if (!formData.email) {
      showMessageAlert('❌ Email Address is required! Please enter your email.', 'error');
      return;
    }
    
    if (!formData.phone) {
      showMessageAlert('❌ Phone Number is required! Please enter your phone number.', 'error');
      return;
    }
    
    if (formData.email && !formData.email.includes('@')) {
      showMessageAlert('❌ Invalid Email! Please enter a valid email address with @', 'error');
      return;
    }
    
    if (formData.phone && formData.phone.length < 10) {
      showMessageAlert('❌ Invalid Phone! Phone number must be at least 10 digits.', 'error');
      return;
    }

    showMessageAlert('✅ Success! Entry form submitted successfully! 🎉', 'success');
    
    console.log('Form Data Submitted:', formData);
    
    setTimeout(() => {
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        projectType: '',
        budget: '',
        priority: 'medium',
        description: '',
        category: '',
        status: 'pending',
        autocompleteValue: '',
        selectedImage: null,
        imagePreview: null,
        projectDate: null
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }, 2000);
  };

  const handleReset = () => {
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      company: '',
      projectType: '',
      budget: '',
      priority: 'medium',
      description: '',
      category: '',
      status: 'pending',
      autocompleteValue: '',
      selectedImage: null,
      imagePreview: null,
      projectDate: null
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    showMessageAlert('Form has been reset', 'success');
  };

  return (
    <>
      <AdminHeader />
      <div className="min-h-screen py-8 px-4 md:px-8 pt-24">
        {/* Insight/Overview Box */}
        <Card className="mb-8 bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl backdrop-blur-sm animate-slide-up" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[rgba(0,168,255,0.3)] rounded-lg flex items-center justify-center" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.5)' }}>
                <Info className="text-[#00a8ff]" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1" style={{ textShadow: '0 0 8px #00a8ff' }}>Entry Form Overview</h3>
                <p className="text-[#00a8ff] text-sm" style={{ textShadow: '0 0 5px #00a8ff' }}>Fill in all required fields. Audio notifications and message alerts are enabled for all form actions.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Header */}
        <div className="mb-8 animate-slide-up">
          <h1 className="text-4xl font-bold led-text animate-led-pulse mb-2" style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}>
            ENTRY FORM
          </h1>
          <p className="text-[#00a8ff]" style={{ textShadow: '0 0 8px #00a8ff' }}>Create new customer entry</p>
        </div>

        {/* ALWAYS VISIBLE MESSAGE BOX WITH AUDIO ALERT - ENHANCED */}
        <Card className="mb-8 bg-gradient-to-br from-[rgba(0,168,255,0.4)] via-[rgba(30,64,98,0.9)] to-[rgba(0,168,255,0.3)] border-4 border-[#00a8ff] rounded-2xl backdrop-blur-sm animate-slide-up" style={{ boxShadow: '0 0 40px rgba(0,168,255,0.9), inset 0 0 20px rgba(0,168,255,0.3)' }}>
          <CardContent className="p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#00a8ff] rounded-xl flex items-center justify-center animate-pulse" style={{ boxShadow: '0 0 30px rgba(0,168,255,1)' }}>
                <MessageSquare className="text-[#0a2540]" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white led-text mb-1" style={{ textShadow: '0 0 15px #00a8ff, 0 0 25px #4cd3ff' }}>
                  📢 MESSAGE ALERTS (WITH AUDIO) 🔊
                </h3>
                <p className="text-[#00a8ff] text-base font-bold" style={{ textShadow: '0 0 8px #00a8ff' }}>Real-time form notifications with sound alerts</p>
              </div>
            </div>
            <div className="space-y-3 max-h-72 overflow-y-auto custom-scrollbar">
              {messages.map((msg) => (
                <div key={msg.id} className={`p-5 rounded-xl border-3 ${msg.type === 'success' ? 'bg-gradient-to-r from-[rgba(0,255,0,0.2)] to-[rgba(0,255,0,0.1)] border-green-400' : 'bg-gradient-to-r from-[rgba(255,0,0,0.2)] to-[rgba(255,0,0,0.1)] border-red-400'} flex items-start gap-4 animate-slide-up transform transition-all hover:scale-102`} style={{ boxShadow: `0 0 20px ${msg.type === 'success' ? 'rgba(0,255,0,0.5)' : 'rgba(255,0,0,0.5)'}` }}>
                  {msg.type === 'success' ? (
                    <CheckCircle className="text-green-300 flex-shrink-0 mt-1" size={24} style={{ filter: 'drop-shadow(0 0 10px rgb(74 222 128))' }} />
                  ) : (
                    <AlertCircle className="text-red-300 flex-shrink-0 mt-1" size={24} style={{ filter: 'drop-shadow(0 0 10px rgb(248 113 113))' }} />
                  )}
                  <div className="flex-1">
                    <p className={`text-lg font-bold ${msg.type === 'success' ? 'text-green-200' : 'text-red-200'}`} style={{ textShadow: `0 0 10px ${msg.type === 'success' ? '#4ade80' : '#f87171'}` }}>{msg.text}</p>
                    <p className="text-sm text-[#00a8ff] mt-2 font-semibold" style={{ textShadow: '0 0 5px #00a8ff' }}>⏰ {msg.timestamp}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Entry Form */}
        <Card className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl backdrop-blur-sm animate-slide-up" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)' }}>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} noValidate className="space-y-6">
              {/* Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="fullName" className="text-[#00a8ff] font-bold mb-2 block">Full Name *</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff] h-11"
                    placeholder="Enter full name"
                    style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#00a8ff] font-bold mb-2 block">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff] h-11"
                    placeholder="email@example.com"
                    style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-[#00a8ff] font-bold mb-2 block">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff] h-11"
                    placeholder="+1 (555) 123-4567"
                    style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                  />
                </div>

                <div>
                  <Label htmlFor="company" className="text-[#00a8ff] font-bold mb-2 block">Company Name</Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff] h-11"
                    placeholder="Company name"
                    style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="autocompleteValue" className="text-[#00a8ff] font-bold mb-2 block">Service Category (Autocomplete)</Label>
                  <div className="relative">
                    <Input
                      id="autocompleteValue"
                      name="autocompleteValue"
                      type="text"
                      value={formData.autocompleteValue}
                      onChange={handleAutocompleteChange}
                      onFocus={() => {
                        if (formData.autocompleteValue.length > 0) {
                          const filtered = autocompleteOptions.filter(option =>
                            option.toLowerCase().includes(formData.autocompleteValue.toLowerCase())
                          );
                          setFilteredOptions(filtered);
                          setShowAutocomplete(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowAutocomplete(false), 200);
                      }}
                      className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff] h-11"
                      placeholder="Start typing to search..."
                      style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                    />
                    {showAutocomplete && filteredOptions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-[rgba(10,37,64,0.98)] border-2 border-[#00a8ff] rounded-lg overflow-hidden" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.5)' }}>
                        {filteredOptions.map((option, index) => (
                          <div
                            key={index}
                            onClick={() => handleAutocompleteSelect(option)}
                            className="px-4 py-3 text-white hover:bg-[#00a8ff] hover:text-[#0a2540] cursor-pointer transition-colors border-b border-[rgba(0,168,255,0.2)] last:border-b-0"
                            style={{ textShadow: '0 0 5px #00a8ff' }}
                          >
                            {option}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="budget" className="text-[#00a8ff] font-bold mb-2 block">Budget Range (Select)</Label>
                  <Select
                    value={formData.budget}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, budget: value }))}
                  >
                    <SelectTrigger className="bg-[rgba(10,37,64,0.9)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff] h-11" style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}>
                      <SelectValue placeholder="Select budget range" className="text-white" />
                    </SelectTrigger>
                    <SelectContent className="bg-[rgba(10,37,64,0.98)] border-2 border-[#00a8ff] text-white" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.5)' }}>
                      <SelectItem value="5k-10k" className="text-white hover:bg-[#00a8ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540]">$5,000 - $10,000</SelectItem>
                      <SelectItem value="10k-25k" className="text-white hover:bg-[#00a8ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540]">$10,000 - $25,000</SelectItem>
                      <SelectItem value="25k-50k" className="text-white hover:bg-[#00a8ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540]">$25,000 - $50,000</SelectItem>
                      <SelectItem value="50k-100k" className="text-white hover:bg-[#00a8ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540]">$50,000 - $100,000</SelectItem>
                      <SelectItem value="100k+" className="text-white hover:bg-[#00a8ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540]">$100,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 4 */}
              <div>
                <Label className="text-[#00a8ff] font-bold mb-3 block">Priority Level (Radio Buttons)</Label>
                <RadioGroup
                  value={formData.priority}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                  className="flex flex-wrap gap-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="low" id="low" className="border-[#00a8ff] text-[#00a8ff]" />
                    <Label htmlFor="low" className="text-white cursor-pointer hover:text-[#00a8ff] transition-colors">Low Priority</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="medium" id="medium" className="border-[#00a8ff] text-[#00a8ff]" />
                    <Label htmlFor="medium" className="text-white cursor-pointer hover:text-[#00a8ff] transition-colors">Medium Priority</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="high" id="high" className="border-[#00a8ff] text-[#00a8ff]" />
                    <Label htmlFor="high" className="text-white cursor-pointer hover:text-[#00a8ff] transition-colors">High Priority</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="urgent" id="urgent" className="border-[#00a8ff] text-[#00a8ff]" />
                    <Label htmlFor="urgent" className="text-white cursor-pointer hover:text-[#00a8ff] transition-colors">Urgent</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Row 5 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="status" className="text-[#00a8ff] font-bold mb-2 block">Project Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger className="bg-[rgba(10,37,64,0.9)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff] h-11" style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}>
                      <SelectValue placeholder="Select status" className="text-white" />
                    </SelectTrigger>
                    <SelectContent className="bg-[rgba(10,37,64,0.98)] border-2 border-[#00a8ff] text-white" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.5)' }}>
                      <SelectItem value="pending" className="text-white hover:bg-[#00a8ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540]">Pending</SelectItem>
                      <SelectItem value="in-progress" className="text-white hover:bg-[#00a8ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540]">In Progress</SelectItem>
                      <SelectItem value="completed" className="text-white hover:bg-[#00a8ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540]">Completed</SelectItem>
                      <SelectItem value="on-hold" className="text-white hover:bg-[#00a8ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540]">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Picker with dd-MM-yyyy format and auto-close */}
                <div>
                  <Label className="text-[#00a8ff] font-bold mb-2 block">Project Start Date (dd-MM-yyyy)</Label>
                  <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        className="w-full bg-[rgba(10,37,64,0.9)] border-2 border-[#00a8ff] text-white hover:bg-[rgba(0,168,255,0.2)] focus:ring-2 focus:ring-[#00a8ff] justify-start text-left font-normal h-11"
                        style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                      >
                        <CalendarIcon className="mr-2 h-5 w-5 text-[#00a8ff]" />
                        {formData.projectDate ? (
                          <span className="text-white font-semibold">{format(formData.projectDate, 'dd-MM-yyyy')}</span>
                        ) : (
                          <span className="text-[#00a8ff]">Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[rgba(10,37,64,0.98)] border-2 border-[#00a8ff] rounded-lg" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.6)' }}>
                      <Calendar
                        mode="single"
                        selected={formData.projectDate}
                        onSelect={handleDateSelect}
                        initialFocus
                        className="rounded-md border border-[#00a8ff] bg-[rgba(10,37,64,0.95)] p-2"
                        classNames={{
                          months: 'text-white space-y-2',
                          month: 'space-y-2',
                          caption: 'flex justify-center pt-1 relative items-center text-[#00a8ff] text-sm font-bold',
                          caption_label: 'text-[#00a8ff] text-base font-bold',
                          nav: 'space-x-1 flex items-center',
                          nav_button: 'h-7 w-7 bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] rounded transition-all',
                          nav_button_previous: 'absolute left-1',
                          nav_button_next: 'absolute right-1',
                          table: 'w-full border-collapse text-white',
                          head_row: 'flex',
                          head_cell: 'text-[#00a8ff] rounded-md w-8 font-semibold text-xs',
                          row: 'flex w-full mt-1',
                          cell: 'text-center text-sm p-0 relative [&:has([aria-selected])]:bg-[rgba(0,168,255,0.2)] first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
                          day: 'h-8 w-8 p-0 font-normal text-white hover:bg-[#00a8ff] hover:text-[#0a2540] rounded transition-all text-sm aria-selected:opacity-100',
                          day_selected: 'bg-[#00a8ff] text-[#0a2540] font-bold hover:bg-[#4cd3ff] hover:text-[#0a2540] focus:bg-[#00a8ff] focus:text-[#0a2540] shadow-[0_0_10px_#00a8ff]',
                          day_today: 'bg-[rgba(0,168,255,0.3)] text-white font-semibold border border-[#00a8ff]',
                          day_outside: 'text-gray-600 opacity-50 aria-selected:bg-gray-500/50 aria-selected:text-gray-900',
                          day_disabled: 'text-gray-600 opacity-30',
                          day_range_middle: 'aria-selected:bg-[rgba(0,168,255,0.2)] aria-selected:text-white',
                          day_hidden: 'invisible',
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Row 6: File Upload with FIXED HEIGHT */}
              <div>
                <Label htmlFor="imageUpload" className="text-[#00a8ff] font-bold mb-2 block">Upload Image (File Input with Preview)</Label>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Input
                        ref={fileInputRef}
                        id="imageUpload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white file:bg-[#00a8ff] file:text-[#0a2540] file:border-0 file:px-4 file:py-2.5 file:font-bold file:rounded-md file:mr-4 hover:file:bg-[#4cd3ff] transition-all h-11"
                        style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                      />
                    </div>
                    {formData.selectedImage && (
                      <Button
                        type="button"
                        onClick={removeImage}
                        className="bg-red-500 hover:bg-red-600 text-white h-11 px-6"
                      >
                        <X size={18} className="mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                  
                  {formData.imagePreview && (
                    <div className="relative inline-block">
                      <div className="border-2 border-[#00a8ff] rounded-lg overflow-hidden" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.5)' }}>
                        <img
                          src={formData.imagePreview}
                          alt="Preview"
                          className="max-w-full h-auto max-h-64 object-contain"
                        />
                      </div>
                      <div className="mt-2 text-[#00a8ff] text-sm" style={{ textShadow: '0 0 5px #00a8ff' }}>
                        <Upload size={16} className="inline mr-2" />
                        {formData.selectedImage.name} ({Math.round(formData.selectedImage.size / 1024)} KB)
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Row 7: Description */}
              <div>
                <Label htmlFor="description" className="text-[#00a8ff] font-bold mb-2 block">Project Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={5}
                  className="bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white focus:ring-2 focus:ring-[#00a8ff] resize-none"
                  placeholder="Describe your project requirements in detail..."
                  style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  type="submit"
                  className="bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold py-6 px-8 transition-all duration-300 hover:scale-105"
                  style={{ boxShadow: '0 0 20px #00a8ff' }}
                >
                  <Save className="mr-2" size={20} />
                  Submit Entry
                </Button>
                <Button
                  type="button"
                  onClick={handleReset}
                  className="bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] font-bold py-6 px-8 transition-all duration-300"
                  style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}
                >
                  <X className="mr-2" size={20} />
                  Reset Form
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(10,37,64,0.5);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #00a8ff;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4cd3ff;
        }
      `}</style>
    </>
  );
};
